import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import * as React from 'react';

import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Slider } from '@/components/ui/slider';
import {
  PROPERTY_LABEL,
  PROPERTY_ROW,
  PROPERTY_ROW_INTERACTIVE,
  PROPERTY_UNIT,
  PROPERTY_VALUE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface PropertyOption {
  value: string;
  label: string;
  /**
   * One line under the label, for options whose own text is too long to be a
   * value (a 200-character angle statement) or too terse to be self-explaining.
   * The same row grammar the `+` menus already use.
   */
  description?: string;
}

interface BaseProps {
  label: string;
  className?: string;
  disabled?: boolean;
}

interface ReadonlyProps extends BaseProps {
  variant: 'readonly';
  value: React.ReactNode;
  unit?: string;
}

interface DropdownProps extends BaseProps {
  variant: 'dropdown';
  value: string;
  options: readonly PropertyOption[];
  onChange: (value: string) => void;
  /** Renders each option in its own face — used by the font picker. */
  optionStyle?: (option: PropertyOption) => React.CSSProperties | undefined;
}

interface InputProps extends BaseProps {
  variant: 'input';
  value: string;
  onChange: (value: string) => void;
  onCommit?: (value: string) => void;
  unit?: string;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
}

interface ColorProps extends BaseProps {
  variant: 'color';
  value: string;
  onChange: (hex: string) => void;
}

/**
 * The fifth variant. Opacity, feather and corner radius are FEEL properties —
 * a marketer drags them until the scrim looks right, and typing `38` into a
 * numeric cell is the wrong instrument for that. `onChange` fires continuously
 * while dragging (the caller passes it through as a history-skipping write) and
 * `onCommit` fires once on release, which is what folds ~8 hand-rolled slider
 * rows across the MediaStudio toolbars into one row shape.
 */
interface SliderProps extends BaseProps {
  variant: 'slider';
  value: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  onCommit?: (value: number) => void;
}

export type PropertyRowProps =
  ReadonlyProps | DropdownProps | InputProps | ColorProps | SliderProps;

/**
 * The universal inspector property row — the single most load-bearing piece of
 * the "hairline + fill + one shadow" recipe.
 *
 * A filled 32px row with ZERO border: label left, value right. Deliberately not
 * `InspectorPropertyRow`, which is the divided (border-bottom) list model used
 * by the post/image inspector bodies. Both are correct for their surface — a
 * six-row post inspector reads fine as a divided list, a fourteen-row creative
 * inspector does not. Restyling the existing one would have changed every
 * inspector body in the app for a surface neither of them share.
 *
 * WHY (measured, Hightouch): 32px tall, background oklch(0.9702 0 355),
 * border none, radius 5px, padding 0 10px, 11px label / right-aligned value.
 */
export function PropertyRow(props: PropertyRowProps): JSX.Element {
  const { label, className, disabled } = props;

  /**
   * 🔴 CASCADE GUARD. This row's dropdown is mounted inside the Concept
   * Workbench, which is a workspace `Inspector` — a raw `DialogPrimitive.Content`.
   * A menu portalled to `document.body` is a DOM SIBLING of that content, so
   * dismissing it reads to Radix as a click OUTSIDE the dialog and closes the
   * whole workbench. The hook returns null outside any dialog, where the default
   * body portal is the correct answer, so this is safe on every other mount.
   */
  const menuContainer = useDialogContentContainer();

  if (props.variant === 'readonly') {
    return (
      <div className={cn(PROPERTY_ROW, disabled && 'opacity-50', className)}>
        <span className={PROPERTY_LABEL}>{label}</span>
        <span className="flex min-w-0 items-baseline gap-1">
          <span className={cn(PROPERTY_VALUE, 'truncate')}>{props.value}</span>
          {props.unit ? <span className={PROPERTY_UNIT}>{props.unit}</span> : null}
        </span>
      </div>
    );
  }

  if (props.variant === 'dropdown') {
    const selected = props.options.find((option) => option.value === props.value);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className={cn(
            PROPERTY_ROW,
            PROPERTY_ROW_INTERACTIVE,
            'w-full text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50',
            className,
          )}
        >
          <span className={PROPERTY_LABEL}>{label}</span>
          <span className="flex min-w-0 items-center gap-1">
            <span className={cn(PROPERTY_VALUE, 'truncate')}>{selected?.label ?? props.value}</span>
            <CaretDownIcon className="size-3 shrink-0 text-muted-foreground/60" />
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-44 max-w-80" container={menuContainer}>
          {props.options.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => props.onChange(option.value)}
              className={cn(
                'gap-1.5 rounded-sm px-2 py-1.5 text-caption',
                option.description && 'items-start',
              )}
              style={props.optionStyle?.(option)}
            >
              <CheckIcon
                className={cn(
                  'size-3.5 shrink-0',
                  option.description && 'mt-0.5',
                  option.value === props.value ? 'opacity-100' : 'opacity-0',
                )}
              />
              <span className="min-w-0">
                <span className="block truncate">{option.label}</span>
                {option.description ? (
                  <span className="block text-detail text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (props.variant === 'slider') {
    const min = props.min ?? 0;
    const max = props.max ?? 100;
    return (
      <div className={cn(PROPERTY_ROW, disabled && 'opacity-50', className)}>
        <span className={PROPERTY_LABEL}>{label}</span>
        {/* The track takes the middle, not the whole row. A full-bleed slider
            with the readout underneath would break the label-left / value-right
            rail every other row in this column sits on. */}
        <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
          <Slider
            aria-label={label}
            value={[props.value]}
            min={min}
            max={max}
            step={props.step ?? 1}
            disabled={disabled}
            className="w-20 shrink-0"
            onValueChange={([next]) => props.onChange(next)}
            onValueCommit={([next]) => (props.onCommit ?? props.onChange)(next)}
          />
          <span className={cn(PROPERTY_VALUE, 'w-8 shrink-0')}>{Math.round(props.value)}</span>
          {props.unit ? <span className={PROPERTY_UNIT}>{props.unit}</span> : null}
        </span>
      </div>
    );
  }

  if (props.variant === 'color') {
    return (
      <div className={cn(PROPERTY_ROW, disabled && 'opacity-50', className)}>
        <label className={cn(PROPERTY_LABEL, 'cursor-default')} htmlFor={`colour-${label}`}>
          {label}
        </label>
        <span className="flex min-w-0 items-center gap-2">
          {/* Machine values are set in mono — part of the premium read, and it
              stops a hex from being mistaken for prose. */}
          <input
            id={`colour-${label}`}
            value={props.value}
            disabled={disabled}
            onChange={(event) => props.onChange(event.target.value)}
            spellCheck={false}
            className={cn(
              PROPERTY_VALUE,
              'w-20 bg-transparent font-mono italic outline-none placeholder:text-muted-foreground/50',
            )}
          />
          <span className="relative size-4 shrink-0 overflow-hidden rounded-sm border border-border/60">
            <span
              aria-hidden
              className="absolute inset-0"
              style={{ backgroundColor: props.value }}
            />
            <input
              type="color"
              aria-label={`${label} swatch`}
              disabled={disabled}
              value={normaliseHex(props.value)}
              onChange={(event) => props.onChange(event.target.value)}
              className="absolute inset-0 size-full cursor-pointer opacity-0"
            />
          </span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        PROPERTY_ROW,
        'transition-colors focus-within:bg-muted-foreground/10',
        disabled && 'opacity-50',
        className,
      )}
    >
      <label className={cn(PROPERTY_LABEL, 'cursor-default')} htmlFor={`field-${label}`}>
        {label}
      </label>
      <span className="flex min-w-0 items-baseline gap-1">
        <input
          id={`field-${label}`}
          value={props.value}
          disabled={disabled}
          placeholder={props.placeholder}
          inputMode={props.inputMode}
          onChange={(event) => props.onChange(event.target.value)}
          onBlur={(event) => props.onCommit?.(event.target.value)}
          data-slot="property-input"
          className={cn(
            PROPERTY_VALUE,
            'w-full min-w-0 bg-transparent outline-none placeholder:text-muted-foreground/50',
          )}
        />
        {props.unit ? <span className={PROPERTY_UNIT}>{props.unit}</span> : null}
      </span>
    </div>
  );
}

/** `<input type="color">` only accepts `#rrggbb`; anything else resets it to black. */
function normaliseHex(value: string): string {
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return '#000000';
}
